// A long list that draws only the rows on screen (TEMPLATES.md, ListScreen).
//
// IN THE KIT because it positions each row by a measured offset, which is an
// inline style by nature; a clean screen (eslint.config.js, CLEAN_SCREENS)
// has none, and asks the kit for the list instead.

import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

export function VirtualList<T>({
  rows,
  renderRow,
  getKey,
  estimateSize,
  scroller,
}: {
  rows: readonly T[];
  renderRow: (row: T, index: number) => ReactNode;
  getKey: (row: T) => string;
  estimateSize: number;
  /**
   * The element that scrolls: the region the list sits in, or on a phone the
   * whole screen (UX-38), where the header and filters come before it.
   */
  scroller: RefObject<HTMLElement | null>;
}) {
  // HOW FAR DOWN THE SCROLL THE LIST STARTS. With the screen scrolling, the
  // header, the strip and the column names come first, and a list that
  // counted its rows from the top of the scroll drew the wrong ones on screen
  // by their height. Anything above it changing size moves it, so all of that
  // is watched.
  const box = useRef<HTMLDivElement>(null);
  const [margin, setMargin] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    const sc = scroller.current;
    if (el === null || sc === null) return;
    const measure = () => {
      const m = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
      setMargin((prev) => (Math.abs(prev - m) < 0.5 ? prev : m));
    };
    measure();
    const sizes = new ResizeObserver(measure);
    for (const child of Array.from(sc.children)) sizes.observe(child);
    for (const sibling of Array.from(el.parentElement?.children ?? [])) {
      if (sibling !== el) sizes.observe(sibling);
    }
    return () => sizes.disconnect();
  }, [scroller]);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => estimateSize,
    overscan: 8,
    scrollMargin: margin,
  });
  // `flexShrink: 0` IS LOAD-BEARING. The region is a flex column, so this
  // container — whose height IS the virtualizer's measurement — was being
  // shrunk to fit beside its siblings (686px asked, 597px given). The rows are
  // absolutely positioned and so overflowed it (measured 2026-09-22).
  return (
    <div
      ref={box}
      style={{ height: virtualizer.getTotalSize(), position: 'relative', flexShrink: 0 }}
    >
      {virtualizer.getVirtualItems().map((v) => {
        const row = rows[v.index]!;
        return (
          <div
            key={getKey(row)}
            data-index={v.index}
            ref={virtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              // A row's start counts the margin; the box it is drawn in does not.
              transform: `translateY(${v.start - margin}px)`,
            }}
          >
            {renderRow(row, v.index)}
          </div>
        );
      })}
    </div>
  );
}
